import React, { useState } from "react";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { PaziaLoader } from "components/atoms";
import { useKsefLogs, KSeFLogDto, KSeFLogType } from "api/ksef";

const logTypeLabel: Record<KSeFLogType, string> = {
  [KSeFLogType.SubmissionQueued]: "Zakolejkowano",
  [KSeFLogType.SubmissionSent]: "Wysłano",
  [KSeFLogType.SubmissionAccepted]: "Zaakceptowano",
  [KSeFLogType.SubmissionRejected]: "Odrzucono",
  [KSeFLogType.SubmissionError]: "Błąd wysyłki",
  [KSeFLogType.UpoRetrieved]: "UPO pobrano",
  [KSeFLogType.UpoRetrievalFailed]: "Błąd UPO",
  [KSeFLogType.RetryScheduled]: "Ponowna próba",
  [KSeFLogType.RetryAttempt]: "Próba ponowienia",
  [KSeFLogType.SessionStarted]: "Sesja otwarta",
  [KSeFLogType.SessionEnded]: "Sesja zamknięta",
  [KSeFLogType.ApiConnectionTested]: "Test połączenia",
  [KSeFLogType.InfoMessage]: "Info",
  [KSeFLogType.WarningMessage]: "Ostrzeżenie",
  [KSeFLogType.ErrorMessage]: "Błąd",
  [KSeFLogType.XmlGenerationFailed]: "Błąd XML",
};

const logTypeColor: Record<KSeFLogType, string> = {
  [KSeFLogType.SubmissionQueued]: "bg-blue-100 text-blue-700",
  [KSeFLogType.SubmissionSent]: "bg-blue-100 text-blue-700",
  [KSeFLogType.SubmissionAccepted]: "bg-green-100 text-green-700",
  [KSeFLogType.SubmissionRejected]: "bg-red-100 text-red-700",
  [KSeFLogType.SubmissionError]: "bg-red-100 text-red-700",
  [KSeFLogType.UpoRetrieved]: "bg-green-100 text-green-700",
  [KSeFLogType.UpoRetrievalFailed]: "bg-red-100 text-red-700",
  [KSeFLogType.RetryScheduled]: "bg-orange-100 text-orange-700",
  [KSeFLogType.RetryAttempt]: "bg-orange-100 text-orange-700",
  [KSeFLogType.SessionStarted]: "bg-slate-100 text-slate-600",
  [KSeFLogType.SessionEnded]: "bg-slate-100 text-slate-600",
  [KSeFLogType.ApiConnectionTested]: "bg-slate-100 text-slate-600",
  [KSeFLogType.InfoMessage]: "bg-slate-100 text-slate-600",
  [KSeFLogType.WarningMessage]: "bg-yellow-100 text-yellow-700",
  [KSeFLogType.ErrorMessage]: "bg-red-100 text-red-700",
  [KSeFLogType.XmlGenerationFailed]: "bg-red-100 text-red-700",
};

const PAGE_SIZE = 50;

interface LogsTabProps {
  integrationId: number;
}

export const LogsTab = ({ integrationId }: LogsTabProps) => {
  const [skip, setSkip] = useState(0);
  const { isLoading, logs, total } = useKsefLogs(integrationId, skip, PAGE_SIZE);

  const columns = React.useMemo<TanTableColumn<KSeFLogDto>[]>(
    () => [
      {
        id: "createdDate",
        accessorKey: "createdDate",
        header: "Data",
        size: 160,
        enableColumnFilter: false,
        cell: (info) => info.getValue<Date>().toLocaleString(),
      },
      {
        id: "type",
        accessorKey: "type",
        header: "Typ",
        size: 150,
        enableColumnFilter: false,
        cell: (info) => {
          const t = info.getValue<KSeFLogType>();
          return (
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${logTypeColor[t]}`}>
              {logTypeLabel[t]}
            </span>
          );
        },
      },
      {
        id: "actionTitle",
        accessorKey: "actionTitle",
        header: "Tytuł",
        size: 250,
        enableColumnFilter: false,
      },
      {
        id: "details",
        accessorKey: "details",
        header: "Szczegóły",
        size: 300,
        enableColumnFilter: false,
        cell: (info) => info.getValue() ?? "—",
      },
      {
        id: "errorDetails",
        accessorKey: "errorDetails",
        header: "Błąd",
        size: 300,
        enableColumnFilter: false,
        cell: (info) => {
          const val = info.getValue<string | undefined>();
          if (!val) return "—";
          return <span className="text-red-600">{val}</span>;
        },
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(skip / PAGE_SIZE);

  if (isLoading && logs.length === 0) return <PaziaLoader position="center" />;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm text-slate-500">
          {total} wpisów · odświeżanie co 5s
        </span>
        {totalPages > 1 && (
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              className="rounded bg-slate-100 px-3 py-1 text-xs disabled:opacity-40 hover:bg-slate-200"
            >
              Poprzednia
            </button>
            <span className="text-xs text-slate-500">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              disabled={currentPage >= totalPages - 1}
              onClick={() => setSkip(skip + PAGE_SIZE)}
              className="rounded bg-slate-100 px-3 py-1 text-xs disabled:opacity-40 hover:bg-slate-200"
            >
              Następna
            </button>
          </div>
        )}
      </div>
      <TanTableClient data={logs} columns={columns} />
    </div>
  );
};
